//src/pages/InspectionsPage.tsx
import { useState } from 'react';
import { Plus, ClipboardCheck, Calendar, Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/shared/PageHeader';
import { PermissionButton } from '@/components/shared/PermissionButton';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { CrudDialog } from '@/components/shared/CrudDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { mockAreas, mockUsers } from '@/mocks/data';
import { toast } from 'sonner';
import { PERMISSIONS } from '@/lib/permissions';
import { useAuth } from '@/contexts/AuthContext';

interface InspectionLocal {
  id: string; title: string; area: string; status: string; date: string; inspector: string; findings: number;
}

const initialInspections: InspectionLocal[] = [
  { id: '1', title: 'Inspeção Mensal — Produção', area: 'Produção', status: 'concluida', date: '2025-02-15', inspector: 'Ana Souza', findings: 3 },
  { id: '2', title: 'Auditoria NR-12 — Manutenção', area: 'Manutenção', status: 'em_andamento', date: '2025-02-28', inspector: 'Carlos Silva', findings: 1 },
  { id: '3', title: 'Checklist Semanal — Almoxarifado', area: 'Almoxarifado', status: 'pendente', date: '2025-03-05', inspector: 'Ana Souza', findings: 0 },
];

export default function InspectionsPage() {
  const { user } = useAuth();
  const canWrite = user?.permissions?.includes(PERMISSIONS.INSPECTIONS_WRITE) ?? false;

  const [inspections, setInspections] = useState<InspectionLocal[]>(initialInspections);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<InspectionLocal> | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const openNew = () => { if (!canWrite) return; setEditing({ title: '', area: '', status: 'pendente', date: '', inspector: '', findings: 0 }); setDialogOpen(true); };
  const openEdit = (i: InspectionLocal) => { if (!canWrite) return; setEditing({ ...i }); setDialogOpen(true); };
  const openDelete = (id: string) => { if (!canWrite) return; setSelectedId(id); setConfirmOpen(true); };

  const handleSave = () => {
    if (!editing?.title) { toast.error('Título é obrigatório'); return; }
    if (editing.id) {
      setInspections(prev => prev.map(i => i.id === editing.id ? { ...i, ...editing } as InspectionLocal : i));
      toast.success('Inspeção atualizada');
    } else {
      setInspections(prev => [...prev, { ...editing, id: `insp-${Date.now()}` } as InspectionLocal]);
      toast.success('Inspeção criada');
    }
  };

  const handleDelete = () => { if (selectedId) { setInspections(prev => prev.filter(i => i.id !== selectedId)); toast.success('Inspeção removida'); } };

  return (
    <div>
      <PageHeader title="Inspeções e Auditorias" description="Checklists, execução e achados">
        <PermissionButton permission={PERMISSIONS.INSPECTIONS_WRITE} onClick={openNew}><Plus className="mr-2 h-4 w-4" /> Nova Inspeção</PermissionButton>
      </PageHeader>

      <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
        {inspections.map(insp => (
          <Card key={insp.id} className="hover:border-primary/30 transition-colors">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm font-medium">{insp.title}</CardTitle>
                <div className="flex items-center gap-1">
                  <StatusBadge status={insp.status} />
                  <DropdownMenu><DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-7 w-7"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem disabled={!canWrite} onClick={() => openEdit(insp)}><Pencil className="mr-2 h-4 w-4" /> Editar</DropdownMenuItem>
                    <DropdownMenuItem disabled={!canWrite} onClick={() => openDelete(insp.id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" /> Excluir</DropdownMenuItem>
                  </DropdownMenuContent></DropdownMenu>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><ClipboardCheck className="h-3 w-3" /> {insp.area}</div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground"><Calendar className="h-3 w-3" /> {insp.date ? new Date(insp.date).toLocaleDateString('pt-BR') : '—'}</div>
              <div className="flex items-center justify-between mt-2">
                <span className="text-xs text-muted-foreground">Inspetor: {insp.inspector}</span>
                {insp.findings > 0 && <Badge variant="outline" className="text-xs">{insp.findings} achado{insp.findings > 1 ? 's' : ''}</Badge>}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <CrudDialog open={dialogOpen} onOpenChange={setDialogOpen} title={editing?.id ? 'Editar Inspeção' : 'Nova Inspeção'} onSave={handleSave}>
        <div className="space-y-3">
          <div><Label>Título</Label><Input value={editing?.title || ''} onChange={e => setEditing(prev => prev ? { ...prev, title: e.target.value } : prev)} /></div>
          <div><Label>Área</Label>
            <Select value={editing?.area || ''} onValueChange={v => setEditing(prev => prev ? { ...prev, area: v } : prev)}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>{mockAreas.map(a => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Data</Label><Input type="date" value={editing?.date || ''} onChange={e => setEditing(prev => prev ? { ...prev, date: e.target.value } : prev)} /></div>
          <div><Label>Inspetor</Label>
            <Select value={editing?.inspector || ''} onValueChange={v => setEditing(prev => prev ? { ...prev, inspector: v } : prev)}>
              <SelectTrigger><SelectValue placeholder="Selecione..." /></SelectTrigger>
              <SelectContent>{mockUsers.map(u => <SelectItem key={u.id} value={u.name}>{u.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Status</Label>
            <Select value={editing?.status || 'pendente'} onValueChange={v => setEditing(prev => prev ? { ...prev, status: v } : prev)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="pendente">Pendente</SelectItem><SelectItem value="em_andamento">Em Andamento</SelectItem><SelectItem value="concluida">Concluída</SelectItem><SelectItem value="cancelada">Cancelada</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
      </CrudDialog>

      <ConfirmDialog open={confirmOpen} onOpenChange={setConfirmOpen} title="Excluir Inspeção" description="Tem certeza? Esta ação não pode ser desfeita." onConfirm={handleDelete} confirmLabel="Excluir" />
    </div>
  );
}
